(() => {
  if (window.customElements.get('shipping-simulator')) return;

  const STORAGE_KEY = 'chb_cep';
  const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
  const money = (cents) => brl.format(cents / 100);
  const onlyDigits = (value) => (value || '').replace(/\D/g, '').slice(0, 8);
  const formatCep = (digits) => (digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits);
  const readCep = () => {
    try {
      return onlyDigits(window.localStorage.getItem(STORAGE_KEY));
    } catch (error) {
      return '';
    }
  };
  const saveCep = (digits) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, digits);
    } catch (error) {
      /* sem armazenamento: segue sem lembrar o CEP */
    }
  };

  class ShippingSimulator extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.mode = this.dataset.mode || 'cart';
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="cep"]');
      this.button = this.querySelector('button[type="submit"]');
      this.result = this.querySelector('.shipping-simulator__result');
      this.variants = this._parseJson(this.dataset.variants);

      this.input.addEventListener('input', () => {
        this.input.value = formatCep(onlyDigits(this.input.value));
      });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this._calculate();
      });

      const saved = readCep();
      if (saved.length === 8) {
        this.input.value = formatCep(saved);
        const details = this.closest('details');
        if (details) {
          details.addEventListener('toggle', () => {
            if (details.open && !this._done) this._calculate();
          });
        } else {
          this._calculate();
        }
      }
    }

    _parseJson(text) {
      try {
        return JSON.parse(text || '{}');
      } catch (error) {
        return {};
      }
    }

    _show(nodes) {
      this.result.replaceChildren(...nodes);
      this.result.hidden = false;
    }

    _message(text, isError) {
      const p = document.createElement('p');
      p.className = `text-sm ${isError ? 'shipping-simulator__error' : 'text-subdued'}`;
      p.textContent = text;
      return p;
    }

    async _calculate() {
      const cep = onlyDigits(this.input.value);
      if (cep.length !== 8) {
        this._show([this._message('Digite um CEP válido com 8 números.', true)]);
        return;
      }
      this._done = true;
      this.button.disabled = true;
      this._show([this._message('Calculando...', false)]);
      try {
        const address = await this._lookupCep(cep);
        if (!address) {
          this._show([this._message('CEP não encontrado. Confira os números e tente de novo.', true)]);
          return;
        }
        saveCep(cep);
        const cart = await fetch('/cart.js').then((response) => response.json());
        this._render(address, this._rates(cart));
      } catch (error) {
        this._show([this._message('Não foi possível calcular agora. Tente novamente em instantes.', true)]);
      } finally {
        this.button.disabled = false;
      }
    }

    async _lookupCep(cep) {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      if (!response.ok) throw new Error('viacep');
      const data = await response.json();
      if (data.erro) return null;
      return [data.logradouro, data.bairro, `${data.localidade}/${data.uf}`].filter(Boolean).join(', ');
    }

    _currentProductCents() {
      const idField = document.querySelector('form[action*="/cart/add"] [name="id"]');
      const qtyField = document.querySelector('form[action*="/cart/add"] [name="quantity"], input[name="quantity"]');
      const price = this.variants[idField ? idField.value : ''] || Object.values(this.variants)[0] || 0;
      const quantity = Math.max(parseInt(qtyField && qtyField.value, 10) || 1, 1);
      return price * quantity;
    }

    _rates(cart) {
      const threshold = parseInt(this.dataset.freeThreshold, 10) || 0;
      const base = cart.total_price + (this.mode === 'product' ? this._currentProductCents() : 0);
      const free = threshold > 0 && base >= threshold;
      const post = parseInt(this.dataset.postDays, 10) || 0;
      const transit = parseInt(this.dataset.transitDays, 10) || 0;
      return {
        base,
        threshold,
        free,
        post,
        transit,
        name: free ? 'Frete grátis' : this.dataset.rateName || 'Frete',
        price: free ? 0 : parseInt(this.dataset.ratePrice, 10) || 0
      };
    }

    _render(address, rates) {
      const where = document.createElement('p');
      where.className = 'text-sm text-subdued shipping-simulator__address';
      where.textContent = address;

      const list = document.createElement('ul');
      list.className = 'shipping-simulator__options';
      list.setAttribute('role', 'list');
      const item = document.createElement('li');
      const label = document.createElement('span');
      const name = document.createElement('strong');
      name.textContent = rates.name;
      label.append(name);
      const total = rates.post + rates.transit;
      if (total > 0) {
        const prazo = document.createElement('small');
        prazo.textContent = `Entrega em até ${total} dias úteis`;
        label.append(prazo);
      }
      const price = document.createElement('span');
      price.className = rates.price === 0 ? 'shipping-simulator__free-price' : 'shipping-simulator__price';
      price.textContent = rates.price === 0 ? 'Grátis' : money(rates.price);
      item.append(label, price);
      list.append(item);

      const nodes = [where, list];
      if (total > 0) {
        const detail = document.createElement('p');
        detail.className = 'text-xs text-subdued';
        detail.textContent = `Prazo: ${rates.post} dia útil para postar + ${rates.transit} dias úteis de entrega, contados a partir da aprovação do pagamento.`;
        nodes.push(detail);
      }
      if (rates.threshold > 0) {
        const note = document.createElement('p');
        note.className = 'text-sm shipping-simulator__progress';
        note.textContent = rates.free
          ? 'Este pedido tem frete grátis.'
          : `Faltam ${money(rates.threshold - rates.base)} para ganhar frete grátis.`;
        nodes.push(note);
      }
      this._show(nodes);
    }
  }

  window.customElements.define('shipping-simulator', ShippingSimulator);
})();
